import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, Injector, LOCALE_ID, NgModule } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Router, RouterModule, TitleStrategy } from '@angular/router';
import { AppComponent } from './app.component';
import { SharedModule } from '@shared/shared.module';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogModule
} from '@angular/material/dialog';
import { ReactiveFormsModule } from '@angular/forms';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { AppRouterModule } from '@/app/app-router.module';
import { CoreModule } from '@/app/core/core.module';
import { HttpInterceptorsModule } from '@data/_generated-api/http-interceptors/http-interceptors.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import '@angular/common/locales/global/nl-BE';
import { ApplicationReloader } from '@/app/application-reloader';
import { FormlyRootModule } from '@/app/formly-root.module';
import { ApplicationService } from '@/app/application.service';
import { MultiLanguageModule } from '@/app/multi-language.module';
import { AppVersionInitializer } from '@/app/initializers/app-version.initializer';
import { CurrentTabInitializer } from '@/app/initializers/current-tab.initializer';
import { AppGlobalInitializer } from '@/app/initializers/app-global.initializer';
import { AppUpdateAwareErrorHandler } from '@core/app-update/app-update-aware-error-handler';
import { OVERLAY_STACKING_PROVIDER } from '@shared/overlay/overlay-stacking.provider';
import { NG_SELECT_DROPDOWN_PROVIDER } from '@shared/overlay/ng-select-dropdowns.provider';
import { PageTitleStrategy } from '@core/page-title/page-title.strategy';
import { PlatformModeInterceptor } from '@/app/platform/platform-core/platform-mode/platform-mode.interceptor';

@NgModule({
  exports: [],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [BrowserModule,
    CoreModule,
    SharedModule,
    RouterModule,
    ReactiveFormsModule,
    LoggerModule.forRoot({
      level: NgxLoggerLevel.DEBUG,
      colorScheme: ['teal', 'green', 'lightblue', 'white', 'orange', 'red', 'red'],
      enableSourceMaps: true
    }),
    AppRouterModule,
    HttpInterceptorsModule,
    BrowserAnimationsModule,
    FormlyRootModule,
    MatDialogModule,
    MultiLanguageModule],
  providers: [
    // Onafgehandelde fouten naar Sentry; zonder init (lege dsn, zie sentry.ts) is dit
    // een no-op die fouten gewoon naar de console blijft loggen. Eromheen: een verdwenen
    // lazy chunk (tab van vóór een release) vraagt een update-check.
    {
      provide: ErrorHandler,
      deps: [Injector],
      useFactory: (injector: Injector) =>
        new AppUpdateAwareErrorHandler(Sentry.createErrorHandler({ showDialog: false }), injector)
    },
    // Router-instrumentatie voor tracing: zonder deze service krijgen navigaties geen
    // eigen span en blijft er per paginaload maar één transactie over. De initializer
    // dient alleen om de service daadwerkelijk te laten instantiëren.
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    {
      provide: APP_INITIALIZER,
      deps: [Sentry.TraceService],
      multi: true,
      useFactory() {
        return () => undefined;
      }
    },
    ApplicationReloader,
    ApplicationService,
    AppVersionInitializer,
    CurrentTabInitializer,
    {
      provide: APP_INITIALIZER,
      deps: [AppGlobalInitializer],
      multi: true,
      useFactory(appGlobalInitializer: AppGlobalInitializer) {
        return async () => await appGlobalInitializer.init();
      }
    },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: false } }, // Dialog / Pop-Up Config,
    OVERLAY_STACKING_PROVIDER,
    NG_SELECT_DROPDOWN_PROVIDER,
    {
      provide: LOCALE_ID,
      useValue: 'nl-BE'
    },
    // Titel van de browsertab per pagina: "<pagina of chart> · Mechanics in Motion".
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    // Als laatste geregistreerd, dus het dichtst bij de server: ziet de echte HttpErrorResponse
    // vóór UpamErrorInterceptor die omzet (issue #51)
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: PlatformModeInterceptor },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule { }
